/**
 * Discriminador del formulario de póliza.
 *
 * Cada `Policy_type` del backend tiene un campo `kind` que decide qué campos y
 * pestañas se muestran en el detalle de la póliza. Antes esto era un string match
 * frágil (`typePolicy.toLowerCase() === 'aca'`) repartido por varios archivos.
 */

export type TPolicyFormKind =
  | 'ACA'
  | 'LIFE'
  | 'MEDICARE'
  | 'DENTAL'
  | 'VISION'
  | 'SUPPLEMENTAL'
  | 'GENERIC'

export interface IPolicyTypeLike {
  id?: string | number
  kind?: string | null
  typePolicy?: string | null
}

const VALID_KINDS: TPolicyFormKind[] = [
  'ACA',
  'LIFE',
  'MEDICARE',
  'DENTAL',
  'VISION',
  'SUPPLEMENTAL',
  'GENERIC',
]

/**
 * Fallback por nombre para filas de `Policy_type` creadas antes de que existiera
 * `kind` y que todavía no fueron configuradas en el admin. El orden importa:
 * gana la primera coincidencia.
 */
const NAME_FALLBACK: {kind: TPolicyFormKind; keywords: string[]}[] = [
  {kind: 'MEDICARE', keywords: ['medicare', 'advantage', 'medigap']},
  {kind: 'LIFE', keywords: ['life', 'vida', 'final expense', 'gasto final', 'annuit', 'anualidad']},
  {kind: 'DENTAL', keywords: ['dental']},
  {kind: 'VISION', keywords: ['vision', 'vista']},
  {kind: 'ACA', keywords: ['aca', 'marketplace', 'obamacare']},
  {kind: 'SUPPLEMENTAL', keywords: ['supplemental', 'suplement', 'accident', 'indemnity']},
]

export const getPolicyFormKind = (typePolicy?: IPolicyTypeLike | null): TPolicyFormKind => {
  if (!typePolicy) return 'GENERIC'

  const kind = (typePolicy.kind || '').toUpperCase() as TPolicyFormKind
  if (kind && kind !== 'GENERIC' && VALID_KINDS.includes(kind)) return kind

  const name = (typePolicy.typePolicy || '').toLowerCase()
  if (name) {
    const match = NAME_FALLBACK.find(({keywords}) => keywords.some((k) => name.includes(k)))
    if (match) return match.kind
  }

  return VALID_KINDS.includes(kind) ? kind : 'GENERIC'
}

export const isLifeKind = (kind?: TPolicyFormKind | null) => kind === 'LIFE'
export const isAcaKind = (kind?: TPolicyFormKind | null) => kind === 'ACA'

/**
 * Normaliza el id de un `Policy_type` para poder compararlos entre sí.
 *
 * Puede llegar como objeto `{id}` (el valor inicial del formulario), como pk plano
 * o como global id de Relay en base64. `parseId` lanza `InvalidCharacterError` con
 * los dos primeros casos, así que se aísla acá.
 */
export const safePolicyTypeId = (value: any): string => {
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value.id : value
  if (raw === null || raw === undefined || raw === '') return ''
  if (typeof raw === 'number') return String(raw)

  const str = String(raw)
  if (Number(str)) return str // pk plano, p. ej. '3'
  try {
    // Global id de Relay: 'PolicyTypeNode:3' en base64.
    return window.atob(str).split(':')[1] ?? str
  } catch {
    return str
  }
}

/**
 * Categorías de carrier (`Insurance_category.name`) válidas para cada kind.
 * Se usa para acotar el dropdown de carriers del formulario. Un kind sin entrada
 * aquí no filtra nada.
 */
export const CARRIER_CATEGORIES_BY_KIND: Partial<Record<TPolicyFormKind, string[]>> = {
  ACA: ['HEALTH INSURANCE (ACA)'],
  LIFE: ['LIFE INSURANCE', 'FINAL EXPENSE', 'ANNUITIES'],
  MEDICARE: ['MEDICARE'],
  DENTAL: ['DENTAL'],
  VISION: ['VISION'],
  SUPPLEMENTAL: ['SUPPLEMENTAL'],
}

export const PAYMENT_FREQUENCY_OPTIONS = [
  {value: 'MONTHLY', label: 'Monthly'},
  {value: 'QUARTERLY', label: 'Quarterly'},
  {value: 'YEARLY', label: 'Yearly'},
]

export const BENEFICIARY_CLASSES = {
  PRIMARY: 'PRIMARY',
  CONTINGENT: 'CONTINGENT',
} as const

export const BANK_ACCOUNT_TYPE_OPTIONS = [
  {value: 'CHECKING', label: 'Checking'},
  {value: 'SAVINGS', label: 'Savings'},
]
